import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const CvFilteringCreate = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { authToken, project, organization } = useStore();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const steps = [t('cvFiltering.step1'), t('cvFiltering.step2')];

  // Paso 1: Selección de cargo y postulantes
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [applicantInput, setApplicantInput] = useState('');

  // Upload CV
  const [uploadingCv, setUploadingCv] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [currentApplicantForUpload, setCurrentApplicantForUpload] = useState(null);

  // Ponderación de scores
  const [weightCV, setWeightCV] = useState(50);
  const [weightRespuestas, setWeightRespuestas] = useState(50);

  // Proceso de filtrado creado
  const [processing, setProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState(null);

  // Puntos disponibles
  const [pointsData, setPointsData] = useState(null);
  const [loadingPoints, setLoadingPoints] = useState(true);

  useEffect(() => {
    if (project) {
      loadPositions();
      loadApplicants();
    }
  }, [project]);

  // Cargar puntos disponibles para cvFiltering
  useEffect(() => {
    if (!organization) {
      setLoadingPoints(false);
      return;
    }

    const checkPoints = async () => {
      try {
        setLoadingPoints(true);

        const response = await api.get(
          `/organizations/${organization}/usage/check?action=cvFiltering`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.errorCode === 0) {
          const data = response.data.response;

          setPointsData({
            canPerform: data.canPerform,
            remaining: data.remaining,
            limit: data.limit,
            currentUsage: data.currentUsage,
            unlimited: data.unlimited || false,
            reason: data.reason,
            serviceCost: data.serviceCost || 0.1
          });
        } else {
          setPointsData(null);
        }
      } catch (error) {
        console.error('Error checking usage limits:', error);
        setPointsData(null);
      } finally {
        setLoadingPoints(false);
      }
    };

    checkPoints();
  }, [organization, authToken]);

  const loadPositions = async () => {
    try {
      const response = await api.get(
        `/positions?projectId=${project}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        setPositions(response.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading positions:', err);
    }
  };

  const loadApplicants = async () => {
    try {
      const response = await api.get(
        `/applicants?projectId=${project}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        setApplicants(response.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading applicants:', err);
    }
  };

  // Auto-seleccionar postulantes del cargo
  useEffect(() => {
    if (!selectedPosition) {
      setSelectedApplicants([]);
      return;
    }

    const selectedPosId = selectedPosition.id || selectedPosition.uid;

    // Filtrar postulantes que tienen el cargo (con o sin CV)
    const applicantsWithPosition = applicants.filter(applicant => {
      const hasPosition = applicant.positions?.some(p => p.positionId === selectedPosId);
      return hasPosition;
    });

    setSelectedApplicants(applicantsWithPosition);

    if (applicantsWithPosition.length > 0) {
      console.log(`✅ Auto-seleccionados ${applicantsWithPosition.length} postulantes del cargo ${selectedPosition.name}`);
    }
  }, [selectedPosition, applicants]);

  const getSortedApplicants = () => {
    if (!selectedPosition) return applicants;

    const selectedPosId = selectedPosition.id || selectedPosition.uid;

    return [...applicants].sort((a, b) => {
      const aHasPosition = a.positions?.some(p => {
        const match = p.positionId === selectedPosId;
        if (match) {
          console.log('✅ Match found:', a.name, 'has position', p.positionName);
        }
        return match;
      });

      const bHasPosition = b.positions?.some(p => p.positionId === selectedPosId);

      if (aHasPosition && !bHasPosition) return -1;
      if (!aHasPosition && bHasPosition) return 1;
      return 0;
    });
  };

  const handleAddApplicant = (applicant) => {
    // Primero verificar si ya está agregado
    const alreadyAdded = selectedApplicants.find(a => a.id === applicant.id);
    if (alreadyAdded) {
      return;
    }

    // Agregar el postulante (con o sin CV)
    setSelectedApplicants([...selectedApplicants, applicant]);

    // Si no tiene CV, ofrecer subirlo
    if (!applicant.cvUrl) {
      setCurrentApplicantForUpload(applicant);
      setUploadDialogOpen(true);
    }
  };

  const handleRemoveApplicant = (applicantId) => {
    setSelectedApplicants(selectedApplicants.filter(a => a.id !== applicantId));
  };

  const handleUploadCv = async (file) => {
    if (!currentApplicantForUpload) return;

    const allowedExtensions = ['pdf', 'doc', 'docx'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      alert(t('cvFiltering.invalidFileFormat'));
      return;
    }

    try {
      setUploadingCv(currentApplicantForUpload.id);

      const formData = new FormData();
      formData.append('cv', file);

      const response = await api.post(
        `/applicants/${currentApplicantForUpload.id}/cv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data?.response?.status_response === 'OK') {
        const cvUrl = response.data.response.data?.cvUrl || response.data.response.data?.cvURL;

        // Actualizar el applicant en la lista
        const updatedApplicant = { ...currentApplicantForUpload, cvUrl };
        setApplicants(applicants.map(a =>
          a.id === currentApplicantForUpload.id ? updatedApplicant : a
        ));

        // Agregar a seleccionados
        setSelectedApplicants([...selectedApplicants, updatedApplicant]);

        setUploadDialogOpen(false);
        setCurrentApplicantForUpload(null);
      } else {
        alert(t('cvFiltering.uploadCvError'));
      }
    } catch (err) {
      console.error('Error uploading CV:', err);
      alert(t('cvFiltering.uploadCvError'));
    } finally {
      setUploadingCv(null);
    }
  };

  const handleReplaceCv = async (applicant, file) => {
    const allowedExtensions = ['pdf', 'doc', 'docx'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      alert(t('cvFiltering.invalidFileFormat'));
      return;
    }

    try {
      setUploadingCv(applicant.id);

      const formData = new FormData();
      formData.append('cv', file);

      const response = await api.post(
        `/applicants/${applicant.id}/cv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data?.response?.status_response === 'OK') {
        const cvUrl = response.data.response.data?.cvUrl || response.data.response.data?.cvURL;

        // Actualizar en ambas listas
        const updatedApplicant = { ...applicant, cvUrl };
        setApplicants(applicants.map(a =>
          a.id === applicant.id ? updatedApplicant : a
        ));
        setSelectedApplicants(selectedApplicants.map(a =>
          a.id === applicant.id ? updatedApplicant : a
        ));
      } else {
        alert(t('cvFiltering.replaceCvError'));
      }
    } catch (err) {
      console.error('Error replacing CV:', err);
      alert(t('cvFiltering.replaceCvError'));
    } finally {
      setUploadingCv(null);
    }
  };

  const handleWeightCVChange = (value) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) return;
    const clamped = Math.min(100, Math.max(0, parsed));
    setWeightCV(clamped);
    setWeightRespuestas(100 - clamped);
  };

  const handleWeightRespuestasChange = (value) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) return;
    const clamped = Math.min(100, Math.max(0, parsed));
    setWeightRespuestas(clamped);
    setWeightCV(100 - clamped);
  };

  const weightsAreValid = () => weightCV + weightRespuestas === 100;

  const handleNext = () => {
    if (activeStep === 0) {
      if (!selectedPosition) {
        alert(t('cvFiltering.mustSelectPosition'));
        return;
      }
      if (selectedApplicants.length === 0) {
        alert(t('cvFiltering.mustAddApplicant'));
        return;
      }
      if (!weightsAreValid()) {
        alert(t('cvFiltering.weightsError'));
        return;
      }
    }
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  // Calcular costo total de puntos
  const calculateTotalCost = () => {
    return selectedApplicants.length * 0.1;
  };

  // Verificar si tiene puntos suficientes
  const hasEnoughPoints = () => {
    if (!pointsData) return true;
    if (pointsData.unlimited) return true;
    if (!pointsData.canPerform) return false;

    const totalCost = calculateTotalCost();
    // Redondear a 2 decimales para evitar errores de punto flotante
    const remaining = Math.round(pointsData.remaining * 100) / 100;
    const cost = Math.round(totalCost * 100) / 100;

    return remaining >= cost;
  };

  // Verificar si el servicio está disponible en el plan
  const isServiceAvailable = () => {
    if (!pointsData) return true; // No se pudo cargar info
    if (pointsData.reason === 'SERVICE_NOT_AVAILABLE') return false;
    return pointsData.canPerform;
  };

  const handleCreateAndProcess = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Crear el proceso de filtrado
      const createResponse = await api.post(
        '/cv-filterings',
        {
          projectId: project,
          organizationId: organization,
          positionId: selectedPosition.id || selectedPosition.uid,
          positionName: selectedPosition.name,
          title: `${t('cvFiltering.filteringPrefix')} - ${selectedPosition.name}`,
          description: `${t('cvFiltering.processDescription')} ${selectedPosition.name}`,
          applicants: selectedApplicants.map(a => a.id),
          weightCV: weightCV,
          weightRespuestas: weightRespuestas,
          user: authToken,
          lastUpdatedBy: authToken,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (createResponse.data.errorCode !== 0) {
        throw new Error(t('cvFiltering.createProcessError'));
      }

      const filteringId = createResponse.data.response.data.id;

      // 2. Iniciar procesamiento (encolar)
      const processResponse = await api.post(
        `/cv-filterings/${filteringId}/process`,
        { language: 'es' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (processResponse.data.errorCode !== 0) {
        throw new Error(t('cvFiltering.startProcessingError'));
      }

      // 3. Mostrar mensaje de éxito (ya no esperamos a que termine)
      setProcessing(false);
      setProcessStatus('queued');

      // Opcional: redirigir al dashboard después de 3 segundos
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 3000);

    } catch (err) {
      console.error('Error creating and processing:', err);
      setError(err.message || t('cvFiltering.processingError'));
      setProcessing(false);
      setProcessStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box>
      {/* Selección de cargo */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('cvFiltering.selectPosition')}
        </Typography>
        <Autocomplete
          value={selectedPosition}
          onChange={(e, newValue) => setSelectedPosition(newValue)}
          options={positions}
          getOptionLabel={(option) => option.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('cvFiltering.position')}
              placeholder={t('cvFiltering.searchPosition')}
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.industria} - {option.jerarquia}
                </Typography>
              </Box>
            </li>
          )}
        />
      </Paper>

      {/* Agregar postulantes */}
      {selectedPosition && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('cvFiltering.addApplicants')}
            </Typography>

            <Autocomplete
              options={getSortedApplicants().filter(
                applicant => !selectedApplicants.find(a => a.id === applicant.id)
              )}
              getOptionLabel={(option) => option.name || ''}

              inputValue={applicantInput}
              onInputChange={(e, newInputValue) => setApplicantInput(newInputValue)}

              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('cvFiltering.applicant')}
                  placeholder={t('cvFiltering.searchApplicant')}
                  fullWidth
                />
              )}
              onChange={(e, value) => {
                if (value) {
                  handleAddApplicant(value);
                  setApplicantInput('');
                }
              }}
              value={null}

              renderOption={(props, option) => {
                const hasSelectedPosition = option.positions?.some(
                  p => p.positionId === (selectedPosition.id || selectedPosition.uid)
                );
                const hasCv = !!option.cvUrl;

                return (
                  <li {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {hasSelectedPosition && (
                          <Chip label={t('cvFiltering.currentPosition')} size="small" color="primary" />
                        )}
                        {hasCv ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={t('cvFiltering.cvLoaded')}
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip
                            icon={<WarningAmberIcon />}
                            label={t('cvFiltering.noCv')}
                            size="small"
                            color="warning"
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />
          </Paper>

          {/* Lista de postulantes seleccionados */}
          {selectedApplicants.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('cvFiltering.selectedApplicants')} ({selectedApplicants.length})
              </Typography>

              {/* Indicador de puntos */}
              {pointsData && (
                <Alert
                  severity={
                    !isServiceAvailable() ? 'error' :
                      pointsData.unlimited ? 'success' :
                        hasEnoughPoints() ? 'success' : 'warning'
                  }
                  sx={{ mb: 2 }}
                  icon={false}
                >
                  {!isServiceAvailable() ? (
                    <Typography variant="body2" color="error">
                      {t('cvFiltering.serviceNotAvailable')}
                    </Typography>
                  ) : pointsData.unlimited ? (
                    <Typography variant="body2" color="success.main">
                      ✓ {t('cvFiltering.unlimitedPoints')}
                    </Typography>
                  ) : hasEnoughPoints() ? (
                    <Typography variant="body2" color="success.main">
                      ✓ {t('cvFiltering.pointsAvailable')}: {pointsData.remaining.toFixed(1)} {t('cvFiltering.pointsLabel')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="warning.main">
                      ⚠ {t('cvFiltering.insufficientPoints')}: {t('cvFiltering.youHave')} {pointsData.remaining.toFixed(1)} {t('cvFiltering.pointsLabel')}, {t('cvFiltering.need')} {calculateTotalCost().toFixed(1)}
                    </Typography>
                  )}
                </Alert>
              )}

              <List>
                {selectedApplicants.map((applicant) => (
                  <ListItem key={applicant.id} divider>
                    <ListItemText
                      primary={applicant.name}
                      secondary={
                        <Box>
                          {applicant.cvUrl ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={t('cvFiltering.cvLoaded')}
                              size="small"
                              color="success"
                              sx={{ mr: 1 }}
                            />
                          ) : (
                            <Chip
                              label={t('cvFiltering.noCv')}
                              size="small"
                              color="warning"
                              sx={{ mr: 1 }}
                            />
                          )}
                          {applicant.positions && applicant.positions.length > 0 && (
                            <Typography variant="caption" display="block">
                              {t('cvFiltering.positions')}: {applicant.positions.map(p => p.positionName).join(', ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Botón subir/reemplazar CV */}
                        <IconButton
                          component="label"
                          disabled={uploadingCv === applicant.id}
                        >
                          <CloudUploadIcon />
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleReplaceCv(applicant, e.target.files[0]);
                              }
                            }}
                          />
                        </IconButton>

                        {/* Botón eliminar */}
                        <IconButton onClick={() => handleRemoveApplicant(applicant.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </>
      )}

      {/* Configuración de ponderación */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('cvFiltering.scoringWeights')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('cvFiltering.scoringWeightsDescription')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <TextField
            label={t('cvFiltering.cvWeight')}
            value={weightCV}
            onChange={(e) => handleWeightCVChange(e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 100, step: 1 }}
            InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
            sx={{ width: 160 }}
            error={!weightsAreValid()}
          />
          <TextField
            label={t('cvFiltering.answersWeight')}
            value={weightRespuestas}
            onChange={(e) => handleWeightRespuestasChange(e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 100, step: 1 }}
            InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
            sx={{ width: 160 }}
            error={!weightsAreValid()}
            helperText={!weightsAreValid() ? t('cvFiltering.weightsError') : ''}
          />
        </Box>
      </Paper>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      {/* Resumen de costo (simplificado) */}
      {pointsData && selectedApplicants.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('cvFiltering.totalCost')}:</strong> {calculateTotalCost().toFixed(1)} {t('cvFiltering.pointsLabel')}
            {' '}({selectedApplicants.length} {t('cvFiltering.applicantsLabel')})
          </Typography>
          {!pointsData.unlimited && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {t('cvFiltering.remainingAfterProcess')}: {(pointsData.remaining - calculateTotalCost()).toFixed(1)} {t('cvFiltering.pointsLabel')}
            </Typography>
          )}
        </Alert>
      )}

      {/* Resumen del cargo */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('cvFiltering.selectedPosition')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>{t('cvFiltering.name')}:</strong> {selectedPosition.name}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('cvFiltering.industry')}:</strong> {selectedPosition.industria}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('cvFiltering.hierarchy')}:</strong> {selectedPosition.jerarquia}
        </Typography>
        {selectedPosition.descripcion && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{t('cvFiltering.description')}:</strong> {selectedPosition.descripcion}
          </Typography>
        )}
      </Paper>

      {/* Ponderación configurada */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('cvFiltering.scoringWeights')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip label={`${t('cvFiltering.cvWeight')}: ${weightCV}%`} variant="outlined" />
          <Chip label={`${t('cvFiltering.answersWeight')}: ${weightRespuestas}%`} variant="outlined" />
        </Box>
      </Paper>

      {/* Resumen de postulantes */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('cvFiltering.applicantsToAnalyze')} ({selectedApplicants.length})
        </Typography>
        <List>
          {selectedApplicants.map((applicant) => (
            <ListItem key={applicant.id}>
              <DescriptionIcon sx={{ mr: 2, color: 'success.main' }} />
              <ListItemText
                primary={applicant.name}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {applicant.email}
                    </Typography>
                    {applicant.cvUrl ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={t('cvFiltering.cvLoaded')}
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Chip
                        label={t('cvFiltering.noCv')}
                        size="small"
                        color="warning"
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Mensaje de éxito - Proceso encolado */}
      {processStatus === 'queued' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('cvFiltering.successTitle')}
          </Typography>
          <Typography variant="body2">
            {t('cvFiltering.successMessage1')} <strong>{t('cvFiltering.dashboard')}</strong> {t('cvFiltering.successMessage2')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('cvFiltering.redirectingMessage')}
          </Typography>
        </Alert>
      )}

      {/* Error */}
      {processStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('cvFiltering.errorTitle')}
          </Typography>
          <Typography variant="body2">
            {error || t('cvFiltering.errorMessage')}
          </Typography>
        </Alert>
      )}

      {/* Procesando - Creando proceso */}
      {loading && !processStatus && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            <Box>
              <Typography variant="h6">
                {t('cvFiltering.creatingProcess')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('cvFiltering.takeFewSeconds')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}

      {/* Botones de navegación */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0 || processing}
          onClick={handleBack}
        >
          {t('cvFiltering.back')}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleCreateAndProcess}
            disabled={
              loading ||
              processing ||
              processStatus === 'completed' ||
              processStatus === 'queued' ||
              (pointsData && (!isServiceAvailable() || !hasEnoughPoints()))
            }
          >
            {loading ? t('cvFiltering.creating') : processing ? t('cvFiltering.processing') : t('cvFiltering.processCvs')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              !selectedPosition ||
              selectedApplicants.length === 0 ||
              (pointsData && (!isServiceAvailable() || !hasEnoughPoints()))
            }
          >
            {t('cvFiltering.next')}
          </Button>
        )}
      </Box>

      {/* Dialog para subir CV */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploadingCv && setUploadDialogOpen(false)}>
        <DialogTitle>{t('cvFiltering.uploadCv')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('cvFiltering.uploadCvMessage1')} <strong>{currentApplicantForUpload?.name}</strong> {t('cvFiltering.uploadCvMessage2')}
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={!!uploadingCv}
            fullWidth
          >
            {uploadingCv ? t('cvFiltering.uploading') : t('cvFiltering.selectCv')}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleUploadCv(e.target.files[0]);
                }
              }}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setCurrentApplicantForUpload(null);
            }}
            disabled={!!uploadingCv}
          >
            {t('cvFiltering.continueWithoutCv')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CvFilteringCreate;